import type { Address } from '@lmc/ts-common'
import { Button, FormItem, Input, Select } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Spinner } from 'components/generic'
import { useDebounce } from 'hooks/useDebounce/useDebounce'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import useAddressByPostcode, { type AddressType } from './useAddressByPostcode'

type PostcodeAutofillProps = {
  onManuallyChoose: () => void
  isAddingAddressManually: boolean
  fieldName: (string | number)[]
  onAutofill: (values: Partial<Address>) => void
}

export const PostcodeAutofill = ({
  onManuallyChoose,
  isAddingAddressManually,
  fieldName,
  onAutofill
}: PostcodeAutofillProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [postcode, setPostcode] = useState<string | undefined>(undefined)
  const debouncedPostcode = useDebounce<string | undefined>(postcode, 500)
  const { data: addresses, isLoading } = useAddressByPostcode({
    postcode: debouncedPostcode,
    options: {
      enabled: !isAddingAddressManually
    }
  })

  const handleOptionChange = (
    option:
      | { value: string; address: AddressType }[]
      | { value: string; address: AddressType }
  ) => {
    if (Array.isArray(option)) {
      throw new Error(
        'Postcode select was set to multiple. Please change that prop'
      )
    }

    const convertedAddress = convertAddressTypeToAddress(option.address)

    onAutofill({
      ...convertedAddress,
      postcode
    })

    onManuallyChoose()
  }

  const addressesOptions = getOptions(addresses)

  const postcodeFieldName = [...fieldName, 'postcode']

  return (
    <Wrapper>
      <FormItem
        name={postcodeFieldName}
        label={<Text>{t(supConstants.POSTCODE)}</Text>}
      >
        {isAddingAddressManually ? (
          <Input />
        ) : (
          <Select
            showSearch
            labelInValue
            notFoundContent={isLoading ? <Spinner /> : null}
            options={addressesOptions}
            filterOption={false}
            onSearch={(value) => setPostcode(value)}
            onChange={(value, option) => handleOptionChange(option)}
          />
        )}
      </FormItem>
      {isAddingAddressManually || (
        <Button type="button" variant="text" onClick={onManuallyChoose}>
          {t(supConstants.TYPE_IN_ADDRESS_MANUALLY)}
        </Button>
      )}
    </Wrapper>
  )
}

const convertAddressTypeToAddress = (
  address: AddressType
): Partial<Address> => {
  const convertedAddress: Partial<Address> = {
    state: address.country,
    street1: address.line_1,
    street2: address.line_2,
    suburb: address.town_or_city,
    name: address.building_name
  }

  return convertedAddress
}

const getOptions = (addresses: AddressType[] | undefined) =>
  addresses?.map((address) => ({
    value: address.formatted_address.join(', '),
    address
  }))

const Wrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`
